// ผู้ช่วยประจำเวร — matching dental assistants to one doctor shift.
//
// The link itself is an `employee_timetable` row: `doctor_id` = who they assist
// that day, `appointment_id[]` = the shift row(s) they are attached to (the
// column is a LIST; a day can hold two half-day doctors). Both columns already
// existed and were written by Bubble — this app is the second writer, which is
// why the roster proxy carries them forward rather than rewriting them.
//
// Matching rule. A doctor shift accepts a set of appointmentList TYPEs (the
// doctor's `spec`, or the shift's `custom_spec` override) minus the specific
// appointmentList rows they opt out of (`except` / `custom_except`). An
// assistant carries the SAME two columns, meaning "cases I am trained to
// assist". So a good match is one who covers every type the doctor works and
// opts out of nothing the doctor still accepts. Anyone else is still choosable
// — the roster has to be fillable on a bad day — but they are labelled, sorted
// last, and hidden behind a toggle rather than mixed in silently.
import { roleList } from './employee.js'

export const ASSIST_ROLE = 'Assistance'
export const ASSIST_SHIFT_TYPE = 'เวรผู้ช่วย'

export const isAssistant = (e) => roleList(e).some((r) => String(r).trim() === ASSIST_ROLE)

// `except` arrives as raw ids from the record and as `{detail}` objects from the
// endpoints that resolve it through an addon — both shapes, one function.
export function exceptIdSet(raw, apptByDetail) {
  const out = new Set()
  for (const e of (Array.isArray(raw) ? raw : [])) {
    const id = (e && typeof e === 'object')
      ? Number(apptByDetail?.get?.(e.detail)?.id) || 0
      : Number(e) || 0
    if (id) out.add(id)
  }
  return out
}

export const specSet = (rec) => new Set((Array.isArray(rec?.spec) ? rec.spec : []).filter(Boolean).map(String))

/**
 * How well `emp` fits a shift that works `spec` (Set of type strings) and opts
 * out of `exceptIds` (Set of appointmentList ids).
 *
 *   missing  types the doctor works that the assistant is not marked for
 *   refused  cases the assistant opts out of that the doctor still accepts
 *
 * `ok` is both empty. A doctor with no spec at all (plenty of legacy records)
 * has nothing to match against, so everyone is `ok` — an empty requirement is
 * not a failed one.
 */
export function assistMatch(emp, { spec, exceptIds }, { apptById, apptByDetail } = {}) {
  const es = specSet(emp)
  const ee = exceptIdSet(emp?.except, apptByDetail)
  const missing = [...(spec || [])].filter((t) => !es.has(String(t)))
  const refused = [...ee].filter((id) => {
    if (exceptIds?.has?.(id)) return false          // the doctor skips it too
    const type = apptById?.get?.(Number(id))?.type
    return type ? spec?.has?.(String(type)) : false // only counts inside the doctor's own work
  })
  return { ok: missing.length === 0 && refused.length === 0, missing, refused }
}

// "ขาด Ortho, Endo · ไม่รับ ติดเครื่องมือ" — why this person is off-match, in
// the words the picker's hint line has room for.
export function matchNote(m, apptById) {
  const bits = []
  if (m.missing.length) bits.push(`ขาด ${m.missing.join(', ')}`)
  if (m.refused.length) {
    bits.push(`ไม่รับ ${m.refused.map((id) => apptById?.get?.(Number(id))?.detail || `#${id}`).join(', ')}`)
  }
  return bits.join(' · ')
}

/* ── the day's roster rows ───────────────────────────────────────────────── */
export const linksOf = (row) => (Array.isArray(row?.appointment_id) ? row.appointment_id.map(Number).filter(Boolean) : [])
export const isLinkedTo = (row, shiftId) => linksOf(row).includes(Number(shiftId))

// One row per employee for the day — Xano allows only one active row per
// (employee, date), so this is a total map, not a first-wins guess.
export function rosterByEmployee(rows) {
  const m = new Map()
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r?.active === false) continue
    m.set(Number(r.employee_id), r)
  }
  return m
}

export const withLink = (row, shiftId) => [...new Set([...linksOf(row), Number(shiftId)])]
export const withoutLink = (row, shiftId) => linksOf(row).filter((x) => x !== Number(shiftId))
